/* <rc-inline-video> - a muted, looping clip that behaves itself.
 *
 *   <rc-inline-video data-src-desktop="..." data-src-mobile="...">
 *     <video muted loop playsinline preload="none"></video>
 *     <button type="button" aria-pressed="false">...</button>   (optional)
 *   </rc-inline-video>
 *
 * Rules it follows:
 *   - nothing is downloaded until the clip is on screen, and only one file per
 *     device (phones get data-src-mobile)
 *   - reduced motion or data saver: no video at all, the photo underneath stays
 *   - off screen: paused
 *   - autoplay refused (iPhone Low Power Mode, thermal, in-app browsers): the
 *     button switches to "play", and a tap starts it, because a tap is allowed
 *   - the visitor's own pause is remembered; scrolling does not undo it
 *   - class is-playing goes on the <video> once frames are actually running,
 *     which is what fades it in over the photo
 */
(function () {
  if (!window.customElements || window.customElements.get('rc-inline-video')) return;

  class RcInlineVideo extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this._reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      var conn = navigator.connection;
      if (this._reduce.matches || (conn && conn.saveData)) return;

      var el = this;
      var go = function () { if (!el._begun && el.isConnected) { el._begun = true; el.begin(); } };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go, { once: true });
      else go();
    }

    begin() {
      var el = this;
      var video = el.querySelector('video');
      var btn = el.querySelector('button');
      if (!video) return;

      var phone = window.matchMedia('(max-width: 749px)').matches;
      var src = phone
        ? (el.getAttribute('data-src-mobile') || el.getAttribute('data-src-desktop'))
        : (el.getAttribute('data-src-desktop') || el.getAttribute('data-src-mobile'));
      if (!src) return;

      var userPaused = false;

      function setBtn(paused) {
        if (!btn) return;
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      }
      function play() {
        if (!video.getAttribute('src')) { video.muted = true; video.src = src; }
        var p = video.play();
        if (p && p.catch) p.catch(function () { if (video.paused) setBtn(true); });
      }

      video.addEventListener('playing', function () { video.classList.add('is-playing'); setBtn(false); });
      if (btn) {
        btn.hidden = false;
        btn.addEventListener('click', function () {
          if (video.paused) { userPaused = false; play(); }
          else { userPaused = true; video.pause(); setBtn(true); }
        });
      }

      if ('IntersectionObserver' in window) {
        el._io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) { if (!userPaused) play(); }
          else if (video.getAttribute('src')) video.pause();
        }, { rootMargin: '100px' });
        el._io.observe(el);
      } else {
        play();
      }

      el._onReduce = function (e) {
        if (!e.matches) return;
        userPaused = true;
        video.pause();
        video.classList.remove('is-playing');
        if (btn) btn.hidden = true;
        if (el._io) el._io.disconnect();
      };
      if (el._reduce.addEventListener) el._reduce.addEventListener('change', el._onReduce);
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._onReduce && this._reduce.removeEventListener) this._reduce.removeEventListener('change', this._onReduce);
      var video = this.querySelector('video');
      if (video && video.getAttribute('src')) { video.pause(); video.removeAttribute('src'); video.load(); }
    }
  }

  window.customElements.define('rc-inline-video', RcInlineVideo);
})();
